import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { calendarDay } from "./profile";
/**
 * `GET /me/progress?weeks=8&locale=pt` — the Progress screen's aggregates
 * (plan D35, T33).
 *
 * ── SERVER-COMPUTED, BECAUSE THE CLIENT CANNOT ADD THEM UP HONESTLY ─────────
 * Eight weeks of sets summed on the device would be every session and every
 * set pulled over the wire (`T-6` bills the rows either way), and arithmetic
 * that drifts from the server's own definitions — the e1RM formula, the
 * personal-record rule. The muscle chart is impossible on the client at all:
 * set history carries no muscle mapping.
 *
 * ── THE DEFINITIONS LIVE IN ONE PLACE ───────────────────────────────────────
 * `packages/modules/training/src/core/model/progress.ts`. In short:
 * - a week is Monday 00:00 UTC to Monday 00:00 UTC — NOT the streak's
 *   plan-anchored week (`stats.ts`);
 * - a session counts in the week its start falls in, and only once ended;
 * - `sessionsPlanned` is the CURRENT days per week, for every week (the count
 *   in force back then is not stored), `0` with no training config;
 * - `targetSets` is `null` for every muscle when there is no active plan;
 * - `month.personalRecords` uses the session summary's own rule and leaves
 *   out first-ever lifts.
 */
export const PROGRESS_MIN_WEEKS = 1;
export const PROGRESS_MAX_WEEKS = 26;
export const PROGRESS_DEFAULT_WEEKS = 8;
export const progressQuery = z.object({
    /** How many weeks to chart, ending with the current one. A query string
     *  is text, so it is coerced; absent means eight. */
    weeks: z.coerce
        .number()
        .int()
        .min(PROGRESS_MIN_WEEKS)
        .max(PROGRESS_MAX_WEEKS)
        .default(PROGRESS_DEFAULT_WEEKS),
    /** The locale muscle and exercise names are in. English when absent. */
    locale: catalogueLocale.optional(),
});
export const progressWeek = z.object({
    /** The Monday (UTC) the week starts on. Oldest week first. */
    weekStart: calendarDay,
    /** Σ load × reps. A bodyweight set or a hold contributes 0. */
    volumeKg: z.number().nonnegative(),
    sessionsDone: z.int().nonnegative(),
    sessionsPlanned: z.int().nonnegative(),
    /** Σ (end − start) of the week's ended sessions, rounded down. */
    minutes: z.int().nonnegative(),
});
export const progressMuscle = z.object({
    muscleId: z.int().positive(),
    /** In the request's locale. */
    name: z.string().min(1),
    /** This week's sets whose exercise TARGETS the muscle (never synergist). */
    sets: z.int().nonnegative(),
    /** Σ targetSets of the active plan's lines that target it; `null` with no
     *  active plan. */
    targetSets: z.int().nonnegative().nullable(),
});
export const progressLiftPoint = z.object({
    weekStart: calendarDay,
    /** The week's best Epley estimate; all three `null` in a week without it. */
    e1rmKg: z.number().nonnegative().nullable(),
    bestLoadKg: z.number().nonnegative().nullable(),
    bestReps: z.int().nonnegative().nullable(),
});
export const progressLift = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    /** One point per charted week, oldest first. */
    points: z.array(progressLiftPoint),
});
/**
 * A muscle's week in FRACTIONAL sets (`D203`, over `D182`'s rule): a set counts
 * 1 for each target muscle of its exercise and 0.5 for each synergist, warm-ups
 * 0. What the body map lights, against the plan's own weekly volume counted
 * the same way.
 */
export const progressMuscleLoad = z.object({
    muscleId: z.int().positive(),
    name: z.string().min(1),
    /** This week's fractional sets. */
    sets: z.number().nonnegative(),
    /** The active plan's fractional weekly sets for the muscle; `null` with no
     *  active plan. */
    targetSets: z.number().nonnegative().nullable(),
});
/**
 * Working sets by repetitions over the last four weeks (`D204`): 1–5, 6–12,
 * 13–20 and 21 or more. A set without reps (a hold, a distance) is in none.
 * The zones overlap in what they train; the counts do not.
 */
export const progressRepRanges = z.object({
    /** The weeks counted, the current one included. */
    weeks: z.int().positive(),
    strength: z.int().nonnegative(),
    hypertrophy: z.int().nonnegative(),
    endurance: z.int().nonnegative(),
    beyond: z.int().nonnegative(),
});
/** One UTC day of the month that had an ended session (`D204`). */
export const progressDay = z.object({
    day: calendarDay,
    /** Workouts started that day — an abandoned one is not one (`D177`). */
    sessions: z.int().nonnegative(),
    volumeKg: z.number().nonnegative(),
});
/** A span's totals, for "vs período anterior" (`D204`). */
export const progressSpan = z.object({
    sessions: z.int().nonnegative(),
    minutes: z.int().nonnegative(),
    /** Working sets — warm-ups out. */
    sets: z.int().nonnegative(),
    volumeKg: z.number().nonnegative(),
});
export const progressOutput = z.object({
    /** Exactly `weeks` entries, oldest first, the last one the current week. */
    weeks: z.array(progressWeek),
    /** Every muscle with sets or a target this week, in `muscleId` order. */
    thisWeekMuscles: z.array(progressMuscle),
    /** The UTC calendar month containing now. */
    month: z.object({
        sessions: z.int().nonnegative(),
        minutes: z.int().nonnegative(),
        /** Records BEATEN this month — first-ever lifts are not counted. */
        personalRecords: z.int().nonnegative(),
        /** Working sets this month (`D204`). Absent: an older server. */
        sets: z.int().nonnegative().optional(),
        /** Σ load × reps this month, warm-ups out (`D204`). */
        volumeKg: z.number().nonnegative().optional(),
        /** The days that had an ended session, oldest first — the calendar heat
         *  map (`D204`). Absent: an older server. */
        days: z.array(progressDay).optional(),
    }),
    /**
     * The previous month over the SAME span — its first day up to as far into
     * it as `now` is into this one — so a month half gone is never compared
     * with a whole one (`D204`). Absent: an older server.
     */
    monthBefore: progressSpan.optional(),
    /** Fractional sets per muscle this week (`D203`), in `muscleId` order.
     *  Absent: an older server. */
    thisWeekMuscleLoad: z.array(progressMuscleLoad).optional(),
    /** Absent: an older server. */
    repRanges: progressRepRanges.optional(),
    /** At most 4: the exercises with the most sessions in range. */
    lifts: z.array(progressLift),
});
//# sourceMappingURL=progress.js.map